// Public entry point of the zero-dependency .xlsx writer: a WorkbookSpec in, bytes (or a browser
// download) out. Domain-free — the customer workbook's layout lives in engine/xlsx-workbook.ts.

import {
  assertDefinedName,
  assertSheetName,
  contentTypesXml,
  corePropsXml,
  rootRelsXml,
  sheetXml,
  stylesXml,
  workbookRelsXml,
  workbookXml,
} from './ooxml';
import type { DefinedName, SheetSpec } from './ooxml';
import { buildStoredZip } from './zip';
import type { ZipEntry } from './zip';

export type { Cell, DefinedName, ListValidation, NumFmt, SheetSpec, StyleKind } from './ooxml';

export interface WorkbookSpec {
  /** sheets[0] is the active tab. */
  sheets: SheetSpec[];
  definedNames?: DefinedName[];
  /** docProps/core.xml title (what Finder / Drive show as the document title). */
  title?: string;
}

export interface BuildOptions {
  /** Injected clock for deterministic output in tests. */
  now?: Date;
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function validate(book: WorkbookSpec): void {
  if (!book.sheets.length) throw new Error('workbook needs at least one sheet');
  const seen = new Set<string>();
  for (const s of book.sheets) {
    assertSheetName(s.name);
    const key = s.name.toLowerCase();
    if (seen.has(key)) throw new Error(`duplicate sheet name: ${s.name}`);
    seen.add(key);
  }
  const names = new Set<string>();
  for (const d of book.definedNames ?? []) {
    assertDefinedName(d.name);
    const key = d.name.toLowerCase();
    if (names.has(key)) throw new Error(`duplicate defined name: ${d.name}`);
    names.add(key);
    if (!seen.has(d.sheet.toLowerCase())) throw new Error(`defined name ${d.name} targets unknown sheet ${d.sheet}`);
  }
}

/** Every part of the package, in zip order. Exposed for tests; callers want buildXlsx(). */
export function workbookParts(book: WorkbookSpec, now: Date): ZipEntry[] {
  validate(book);
  const enc = new TextEncoder();
  const n = book.sheets.length;
  const parts: Array<[string, string]> = [
    ['[Content_Types].xml', contentTypesXml(n)],
    ['_rels/.rels', rootRelsXml()],
    ['docProps/core.xml', corePropsXml(book.title ?? 'Workbook', now)],
    ['xl/workbook.xml', workbookXml(book.sheets.map((s) => s.name), book.definedNames ?? [])],
    ['xl/_rels/workbook.xml.rels', workbookRelsXml(n)],
    ['xl/styles.xml', stylesXml()],
    ...book.sheets.map((s, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, sheetXml(s, i === 0)]),
  ];
  return parts.map(([name, xml]) => ({ name, data: enc.encode(xml) }));
}

export function buildXlsx(book: WorkbookSpec, opts: BuildOptions = {}): Uint8Array<ArrayBuffer> {
  const now = opts.now ?? new Date();
  return buildStoredZip(workbookParts(book, now), now);
}

/** Browser download via the Blob + anchor idiom the calculator already uses for its CSV/Markdown. */
export function downloadXlsx(book: WorkbookSpec, filename: string): void {
  const bytes = buildXlsx(book);
  const blob = new Blob([bytes], { type: XLSX_MIME });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
